/* eslint-disable no-nested-ternary */

import React, { useState, useCallback, useContext, useEffect } from 'react';
import {
  TableCell,
  TableRow,
  Tooltip,
  IconButton,
  Checkbox,
  Collapse,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import CancelIcon from '@material-ui/icons/Cancel';
import styled from 'styled-components';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { formatDateMMDDYY } from '@mummssoftware/utils/formatters';
import { parseDateMMDDYYYYSlashes } from '@mummssoftware/utils/dates';
import { SuggestBox } from '@mummssoftware/common-ui';
import { FormattedMessage } from 'react-intl';
import { PNCounterIcon } from '@mummssoftware/global-context/PNCounter';
import { PNGlobalContext } from '@mummssoftware/global-context';
import { ENTITY_MEDICATION } from '@mummssoftware/global-context/PNCounter/constants';
import { STRING_TRUE, STRING_FALSE } from '@mummssoftware/utils/constants';
import { orderingPhysicianLabel } from '@mummssoftware/utils/medications/utils';
import { messages } from './messages';
import { CoveredDialog } from './CoveredDialog';
import { AppContext } from '../../App/AppContext';
import PrintMedicationButton from '../Print/PrintMedicationButton';
import {
  getCoveredTooltipTitle,
  getCoveredPlaceholder,
  coveredOptions,
} from './constants';
import { MedContext } from '../../App/MedContext';
import HistoryMedicationsTable from '../History/HistoryMedicationsTable';
import {
  isNeedsApprovalMed,
  showMedsOrderIcon,
  showStartReason,
} from '../../utils/utils';
import { StartReasonPopover } from './StartReasonPopover';
import { HighRiskToggle } from './HighRiskToggle';
import { ADD, REMOVE, HEADER_CELL_COUNT } from '../../App/constants';
import { MedPhysicianPicker } from '../common/MedPhysicianPicker';

const StyledTableCell = styled(TableCell)`
  && {
    color: inherit;
    white-space: nowrap;
    ${(props) =>
      props.smallwidget
        ? `padding: 2px;
           font-size: 14px;`
        : `padding: 1px;
           font-size: 15px;`}
  }
`;
const StyledTruncateTableCell = styled(TableCell)`
  && {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 1px;
    ${(props) =>
      props.smallwidget
        ? `padding: 2px;
           font-size: 14px;`
        : `padding: 3px;
           font-size: 15px;`}
    color: inherit;
  }
`;

const SmallDiv = styled.div`
  width: 40px;
`;

const BlankIconDiv = styled.div`
  min-width: 25px;
`;

const BlankDateDiv = styled.div`
  min-width: 60px;
`;

const ToggleDiv = styled.div`
  ${(props) =>
    props.isMobile && !props.isMobileEditable ? `;` : `cursor: pointer;`}
  width: 40px;
  display: flex;
  justify-content: center;
`;

const StyledTooltip = withStyles({
  tooltip: {
    color: 'white',
    backgroundColor: 'gray',
    fontSize: '1em',
  },
})(Tooltip);

const BaseMedication = ({
  className,
  classes,
  medication,
  handleChange,
  patient,
  updateApprovedMedsHash,
  showCheckmarks,
  showStartReasonColumn = true,
}) => {
  const { progressNotesGlobal } = useContext(PNGlobalContext);

  const [openCoveredDialog, setOpenCoveredDialog] = useState(false);
  const [open, setOpen] = React.useState(false);

  const handleTooltipOpen = () => {
    setOpen(!open);
  };

  const coveredError = medication.coveredbyhospice === '';

  const handleCloseCoveredDialog = () => {
    setOpenCoveredDialog(false);
  };

  const checkDiscontinued = useCallback(() => {
    if (medication.stopped) {
      return classes.medInfoValue;
    }
    return '';
  }, [medication.stopped, classes.medInfoValue]);

  const onCoveredByHospiceChange = useCallback(
    async (e) => {
      const { value } = e;
      if (value === 'true') {
        handleChange(medication, 'coveredbyhospice', {
          coveredbyhospice: value,
          notcoveredreason: '',
        });
      } else {
        setOpenCoveredDialog(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleChange, medication],
  );

  const toggleCoveredByHospice = () => {
    // the med is currently NOT covered
    const isNotCovered = medication.coveredbyhospice === 'false';
    // by toggling we are setting it to covered
    if (isNotCovered) {
      handleChange(medication, 'coveredbyhospice', {
        coveredbyhospice: 'true',
        notcoveredreason: '',
      });
    } else {
      setOpenCoveredDialog(true);
    }
  };

  const {
    readonly,
    isMobile,
    smallWidget,
    rcopiaConfig,
    isInMedOrdersBinList,
  } = useContext(AppContext);

  const { iscoverededitable, hidecovered } = rcopiaConfig;

  const { isMobileEditable } = useContext(MedContext);

  const readonlyAction = () => null;

  const handleActionChange = (action) =>
    readonly || medication.isprescription
      ? readonlyAction
      : isMobile && !isMobileEditable
      ? readonlyAction
      : action;

  const [checked, setChecked] = useState(false);
  const [disableCheckcheck, setDisableCheckbox] = useState(false);

  useEffect(() => {
    if (showCheckmarks === false) {
      setChecked(false);
    }
  }, [showCheckmarks]);

  useEffect(() => {
    if (
      !medication.orderingphysicianid ||
      medication.orderingphysicianid === ''
    ) {
      setDisableCheckbox(true);
    } else {
      setDisableCheckbox(false);
    }
  }, [medication.orderingphysicianid]);

  const handleApprovalCheckboxChange = (event) => {
    if (event.target.checked === true) {
      setChecked(true);
      updateApprovedMedsHash(medication, ADD);
    } else {
      setChecked(false);
      updateApprovedMedsHash(medication, REMOVE);
    }
  };

  /**
   * Returns the column count based on the conditional hiden columns
   */
  const getColumnCount = () => {
    let columnCount = HEADER_CELL_COUNT;
    if (hidecovered === STRING_TRUE) columnCount--;
    if (!showStartReasonColumn) columnCount -= 2;

    return columnCount;
  };

  const medAtAdmit =
    medication &&
    medication.medatadmit &&
    medication.medatadmit.toLowerCase() === STRING_TRUE;

  // TODO:  refactor out approval workflow once all sites are live with med orders.
  // Conditionally show approval ui elements if site is still using the old approval
  // workflow.
  const showNeedsApprovalUI =
    !isInMedOrdersBinList && !showCheckmarks && isNeedsApprovalMed(medication);

  const showApprovalUICheckbox =
    !isInMedOrdersBinList && showCheckmarks && !medAtAdmit;

  const isDisabled =
    readonly ||
    medication.isprescription ||
    iscoverededitable === 'false' ||
    (isMobile && !isMobileEditable);

  const showCovered = hidecovered === STRING_FALSE || !hidecovered;
  const showStopReason =
    medication.stopreason &&
    !medication.hideStopReason &&
    !medication.isprescription;

  const isFromMedOrder = showMedsOrderIcon(medication, progressNotesGlobal);
  const showStartReasonField =
    showStartReasonColumn && showStartReason(medication);

  // TODO change the logic
  return smallWidget ? (
    <TableRow
      hover
      tabIndex={-1}
      key={medication.sig}
      className={`${className} ${checkDiscontinued()}`}
    >
      {/* Progress Note Counts */}
      <StyledTableCell>
        <PNCounterIcon
          patientNumber={patient.patientNumber}
          entityKey={medication.externalid}
          entityName={medication.drugdescription}
          entityType={ENTITY_MEDICATION}
        />
      </StyledTableCell>
      {/* NEEDS APPROVAL */}
      {showNeedsApprovalUI ? (
        <StyledTooltip title={<FormattedMessage {...messages.needsApproval} />}>
          <StyledTableCell align="center">
            <FiberManualRecordIcon color="error" fontSize="small" />
          </StyledTableCell>
        </StyledTooltip>
      ) : (
        !showCheckmarks && <StyledTableCell />
      )}
      {/* MEDICATION NAME */}
      <StyledTooltip
        title={medication.drugdescription}
        aria-label="medication name"
      >
        <StyledTruncateTableCell
          align="left"
          smallwidget={
            smallWidget && smallWidget.toString()
              ? smallWidget.toString()
              : undefined
          }
        >
          {medication.drugdescription}
        </StyledTruncateTableCell>
      </StyledTooltip>
      {/* MEDICATION STARTDATE */}
      <StyledTableCell
        align="left"
        smallwidget={
          smallWidget && smallWidget.toString()
            ? smallWidget.toString()
            : undefined
        }
      >
        {medication.startdate_shifted
          ? formatDateMMDDYY(
              parseDateMMDDYYYYSlashes(medication.startdate_shifted),
            )
          : ''}
      </StyledTableCell>
    </TableRow>
  ) : (
    <>
      <TableRow
        hover
        tabIndex={-1}
        key={medication.sig}
        className={checkDiscontinued()}
      >
        {/* HISTORY MEDICATION */}
        {medication.history ? (
          <StyledTableCell>
            <IconButton
              aria-label="print row"
              size="small"
              // color="primary"
              onClick={handleTooltipOpen}
            >
              {!open ? (
                <KeyboardArrowRightIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          </StyledTableCell>
        ) : (
          <StyledTableCell>
            <BlankIconDiv />
          </StyledTableCell>
        )}
        {/* Progress Note Counts */}
        <StyledTableCell>
          <PNCounterIcon
            patientNumber={patient.patientNumber}
            entityKey={medication.externalid}
            entityName={medication.drugdescription}
            entityType={ENTITY_MEDICATION}
          />
        </StyledTableCell>
        {/* NEEDS APPROVAL */}
        {showNeedsApprovalUI ? (
          <StyledTooltip
            title={<FormattedMessage {...messages.needsApproval} />}
          >
            <StyledTableCell align="center">
              <FiberManualRecordIcon color="error" fontSize="small" />
            </StyledTableCell>
          </StyledTooltip>
        ) : (
          !showCheckmarks && (
            <StyledTableCell>
              <BlankIconDiv />
            </StyledTableCell>
          )
        )}
        {showApprovalUICheckbox ? (
          <StyledTableCell>
            <Checkbox
              checked={checked}
              disabled={disableCheckcheck}
              onChange={(e) => handleApprovalCheckboxChange(e)}
            />
          </StyledTableCell>
        ) : null}
        {/* Non Order Med */}
        <StyledTooltip
          title={<FormattedMessage {...messages.orderColumnToolTipMsg} />}
          aria-label={<FormattedMessage {...messages.orderColumnToolTipMsg} />}
        >
          <StyledTableCell align="center">
            {isFromMedOrder ? 'Y' : 'N'}
          </StyledTableCell>
        </StyledTooltip>
        {/* MEDICATION NAME */}
        <StyledTooltip
          title={medication.drugdescription}
          aria-label="medication name"
        >
          <StyledTruncateTableCell align="left">
            {medication.drugdescription}
          </StyledTruncateTableCell>
        </StyledTooltip>
        {/* MEDICATION INSTRUCTIONS */}
        <StyledTooltip title={medication.sig}>
          <StyledTruncateTableCell
            align="left"
            aria-label="medication description"
          >
            {medication.sig}
          </StyledTruncateTableCell>
        </StyledTooltip>
        {/* MEDICATION QUANTITY */}
        <StyledTableCell align="center">
          {Math.trunc(medication.quantity)}
        </StyledTableCell>
        {/* MEDICATION STARTDATE */}
        <StyledTableCell align="left">
          {medication.startdate_shifted
            ? formatDateMMDDYY(
                parseDateMMDDYYYYSlashes(medication.startdate_shifted),
              )
            : ''}
        </StyledTableCell>
        {/* COVERED BY HOSPICE */}
        {showCovered ? (
          <StyledTableCell>
            {coveredError ? (
              <SmallDiv>
                <SuggestBox
                  inputId={`covered-${medication.id}`}
                  hasError={coveredError}
                  fitcontent
                  placeholder={getCoveredPlaceholder(medication)}
                  tooltipTitle={getCoveredTooltipTitle(medication) || ''}
                  options={coveredOptions}
                  disabled={isDisabled}
                  onChange={handleActionChange(onCoveredByHospiceChange)}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </SmallDiv>
            ) : (
              <ToggleDiv
                isMobile={isMobile}
                isMobileEditable={isMobileEditable}
                onClick={handleActionChange(toggleCoveredByHospice)}
              >
                <StyledTooltip
                  title={getCoveredTooltipTitle(medication) || ''}
                  aria-label="med-covered"
                >
                  <div>{getCoveredPlaceholder(medication)}</div>
                </StyledTooltip>
              </ToggleDiv>
            )}
            <CoveredDialog
              open={openCoveredDialog}
              onClose={handleCloseCoveredDialog}
              classes={classes}
              medication={medication}
              handleChange={handleChange}
            />
          </StyledTableCell>
        ) : null}
        {/* PHYSICIAN */}
        <MedPhysicianPicker
          medication={medication}
          handleChange={handleChange}
          isFromMedOrder={isFromMedOrder}
        />
        {/* MEDICATION STOPDATE */}
        {medication.stopdate_shifted ? (
          <StyledTooltip title={medication.stopreason} aria-label="stop reason">
            <StyledTableCell align="center">
              {formatDateMMDDYY(
                parseDateMMDDYYYYSlashes(medication.stopdate_shifted),
              )}
            </StyledTableCell>
          </StyledTooltip>
        ) : (
          <StyledTableCell>
            <BlankDateDiv />
          </StyledTableCell>
        )}
        {/* STOP REASON */}
        <StyledTableCell align="center">
          {showStopReason ? (
            <StyledTooltip
              title={medication.stopreason}
              aria-label="stop reason"
            >
              <span>
                <CancelIcon
                  className={classes.discontinuedIcon}
                  fontSize="small"
                />
              </span>
            </StyledTooltip>
          ) : (
            <BlankIconDiv />
          )}
        </StyledTableCell>
        {/* HIGH RISK INDICATOR */}
        {showStartReasonColumn ? (
          <StyledTableCell align="center">
            {showStartReasonField ? (
              <HighRiskToggle
                medication={medication}
                handleChange={handleChange}
                readonly={readonly}
              />
            ) : (
              <BlankIconDiv />
            )}
          </StyledTableCell>
        ) : null}
        {/* START REASON */}
        {showStartReasonColumn ? (
          <StyledTableCell align="center">
            {showStartReasonField ? (
              <StartReasonPopover
                medication={medication}
                handleChange={handleChange}
                readonly={readonly}
              />
            ) : (
              <BlankIconDiv />
            )}
          </StyledTableCell>
        ) : null}
        {/* PRINT MEDICATION */}
        <StyledTableCell align="center">
          <PrintMedicationButton
            patient={patient}
            medication={medication}
            orderingPhysicianLabel={orderingPhysicianLabel}
          />
        </StyledTableCell>
      </TableRow>
      {medication.history ? (
        <TableRow>
          <TableCell colSpan={getColumnCount()}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <HistoryMedicationsTable
                externalid={medication.externalid}
                showCheckmarks={showCheckmarks}
                updateApprovedMedsHash={updateApprovedMedsHash}
                handleChange={handleChange}
              />
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};

export const Medication = withStyles((theme) => ({
  overrides: {
    MuiTableCell: {
      root: {
        paddingTop: '6px',
      },
      paddingDefault: {
        padding: '6px',
      },
    },
  },
  hide: {
    display: 'none',
  },
  medInfo: {
    display: 'block',
    color: theme.palette.text.hint,
  },
  medInfoValue: {
    color: theme.palette.text.secondary,
  },
  discontinuedIcon: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
    fontWeight: '700',
  },
}))(React.memo(BaseMedication));
